import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadingSpinner } from "@/components/ui/spinner";
import {
  TypographyMuted,
  TypographyP,
  TypographySmall,
} from "@/components/ui/typography";

import { AddPagesViewer } from "./components/add-pages-viewer";
import { EditPdfConfirmationDialog } from "./components/confirm-dialog";
import { SortablePageContainer, SplitAction } from "./components/page";
import { PagesViewer } from "./components/pages-viewer";
import { usePageManagement } from "./hooks/use-page-management";
import type { EditablePage } from "./types";
import { getPdfPages } from "./utils/get-pdf-pages";
import { generatePreviewPdf } from "./workers/generate-pdf-preview/client";

const LoadingEditPdfSkeleton = () => {
  return (
    <div className="h-full flex flex-row gap-2 p-1">
      <div
        className="pl-2"
        style={{
          width: `${100 / Math.SQRT2}vh`,
        }}
      >
        <div className="h-full flex flex-col gap-2 py-1">
          <div className="flex-1 relative overflow-auto grid place-items-center px-4">
            <Skeleton className="w-full h-full outline outline-gray-300" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-4 px-4 text-center">
                <LoadingSpinner className="w-12 h-12" />
                <TypographyP className="max-w-md">
                  Das Dokument wird geladen. Bei großen Dokumenten kann dies
                  einen Moment dauern. Sobald das Dokument geladen ist, wird es
                  hier erscheinen.
                </TypographyP>
              </div>
            </div>
          </div>
          <div className="flex flex-row justify-center items-center">
            <div className="flex flex-row border rounded-sm bg-background">
              <Skeleton className="w-[311px] h-[36px] rounded-none" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto pr-2">
        <div
          className="grid gap-y-3 gap-x-0"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          }}
        >
          {Array.from(new Array(20), (el, index) => (
            <div key={`skeleton_${index + 1}`} className="flex flex-row">
              <div className="flex w-full flex-col gap-2 items-center p-1">
                <Skeleton className="w-full aspect-[1/1.4142] outline outline-gray-300" />
                <TypographyMuted>{index + 1}</TypographyMuted>
              </div>
              <SplitAction onAction={() => {}} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PagesPreview = ({
  pages,
  currentPage,
  setCurrentPage,
  setCurrentInvoiceId,
  setPages,
  handleMove,
  currentInvoiceId,
  splits,
  handleSplit,
  handleDeletePage,
  handleAddInvoice,
  isAddingPages,
  setIsAddingPages,
  sourceInvoiceId,
  sensors,
}: {
  pages: EditablePage[];
  currentPage: number;
  setCurrentPage: (pageNumber: number) => void;
  setCurrentInvoiceId: (invoiceId: string | null) => void;
  setPages: (pages: EditablePage[]) => void;
  handleMove: (oldIndex: number, newIndex: number) => void;
  sensors: any;
  currentInvoiceId: string | null;
  splits: number[];
  handleSplit: (pageIndex: number) => void;
  handleDeletePage: (pageIndex: number) => void;
  handleAddInvoice: any;
  isAddingPages: boolean;
  setIsAddingPages: (isAddingPages: boolean) => void;
  sourceInvoiceId: any;
}) => {
  const { t } = useTranslation();

  const latestInvoicePreviewReqId = useRef(0);

  const handleSelectPage = async (pageNumber: number) => {
    setCurrentInvoiceId(null);
    setPages(pages.slice());
    setCurrentPage(pageNumber);
  };

  const handleSelectInvoice = async (invoiceId: string, pdfUrl: string) => {
    setCurrentInvoiceId(invoiceId);
    setCurrentPage(0);

    const allPages = await getPdfPages([
      {
        invoiceId,
        fileName: `${invoiceId}-0-${Date.now()}`,
        pdfUrl,
        thumbnailUrls: [],
      },
    ]);

    setPages(allPages);

    const reqId = ++latestInvoicePreviewReqId.current;

    try {
      const allPages = await getPdfPages([
        {
          invoiceId,
          fileName: `${invoiceId}-0-${Date.now()}`,
          pdfUrl,
          thumbnailUrls: [],
        },
      ]);

      if (reqId !== latestInvoicePreviewReqId.current) return;
      setPages(allPages);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = pages.findIndex((page) => page.id === active.id);
      const newIndex = pages.findIndex((page) => page.id === over?.id);

      let newCurrentPage = currentPage;

      if (currentPage === oldIndex) {
        newCurrentPage = newIndex;
      } else if (oldIndex < newIndex) {
        if (currentPage > oldIndex && currentPage <= newIndex) {
          newCurrentPage = currentPage - 1;
        }
      } else {
        if (currentPage >= newIndex && currentPage < oldIndex) {
          newCurrentPage = currentPage + 1;
        }
      }

      setCurrentPage(newCurrentPage);

      handleMove(oldIndex, newIndex);
    }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto pr-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pages.map((page) => page.id)}
            strategy={rectSortingStrategy}
          >
            <div
              className="grid gap-y-3 gap-x-0"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              }}
            >
              {pages.map((page, index) => (
                <SortablePageContainer
                  key={page.id}
                  page={page}
                  pageIndex={index}
                  focused={currentPage === index && currentInvoiceId === null}
                  splits={splits}
                  onSplit={handleSplit}
                  onSelect={handleSelectPage}
                  onDelete={handleDeletePage}
                  totalPages={pages.length}
                />
              ))}
              <div className="flex flex-row">
                <div className="flex-1 border-gray-100 border-2">
                  <div className="flex flex-col gap-2 p-2 items-center group">
                    <div
                      className="w-full aspect-[1/1.4142] border border-gray-400 rounded-md flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 cursor-pointer text-center"
                      onClick={() => {
                        setIsAddingPages(true);
                      }}
                    >
                      <Plus className="w-12 h-12 text-muted-foreground" />
                      <TypographySmall className="text-muted-foreground">
                        {t("pdf_add_pages")}
                      </TypographySmall>
                    </div>
                  </div>
                </div>
                <div className="h-full w-10" />
              </div>
            </div>
          </SortableContext>
        </DndContext>
      </div>
      {isAddingPages && (
        <div className="absolute right-0 w-[24%] h-[calc(100%-62px)] bg-white border-l border-gray-200 shadow-lg z-10 overflow-hidden">
          <AddPagesViewer
            sourceInvoiceId={sourceInvoiceId}
            excludeInvoiceIds={pages.map((p) => p.sourceInvoiceId)}
            selectedInvoiceId={currentInvoiceId}
            onSelectInvoice={handleSelectInvoice}
            onClose={() => {
              setIsAddingPages(false);
            }}
            onAddInvoice={handleAddInvoice}
          />
        </div>
      )}
    </>
  );
};

type EditPdfProps = {
  pages: EditablePage[];
  sourceInvoiceId?: string;
  isInitializing: boolean;
  onPagesChange: (pages: EditablePage[]) => void;
  onSave: (pages: EditablePage[], splits: number[]) => Promise<void>;
};

export const EditPdf = ({
  pages,
  sourceInvoiceId,
  onPagesChange,
  onSave,
  isInitializing,
}: EditPdfProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingPages, setIsAddingPages] = useState(false);

  const {
    splits,
    splitEvery,
    handleSplit,
    handleDeletePage,
    handleAddInvoice,
    handleMove,
    handleInputChange,
    setSplitEvery,
    splitCount,
  } = usePageManagement({
    pages,
    onPagesChange,
  });

  const handleBack = () => {
    window.history.back();
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await onSave(pages, splits);
    } finally {
      setIsLoading(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-end items-end py-2 px-4 gap-4">
        <Button variant="ghost" className="gap-2" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4" />
          <TypographySmall>{t("back")}</TypographySmall>
        </Button>
        <div className="flex-1" />
        <div className="flex flex-row gap-2 items-center">
          <Checkbox
            checked={splitEvery}
            onCheckedChange={(checked) => setSplitEvery(!!checked)}
          />
          <TypographySmall>{t("pdf_split_every")}</TypographySmall>
          <Input
            onChange={handleInputChange}
            type="number"
            className="w-16"
            defaultValue={1}
          />
          <TypographySmall>{t("pdf_split_every_pages")}</TypographySmall>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => {
            setIsAddingPages(true);
          }}
        >
          <Plus className="w-4 h-4" />
          {t("pdf_add_pages")}
        </Button>
        <EditPdfConfirmationDialog onSubmit={handleSubmit}>
          <DialogTrigger asChild>
            <Button disabled={isLoading} className="gap-2">
              {isLoading && <LoadingSpinner />}
              {t("pdf_count_split", {
                count: splitCount === 0 ? 1 : splitCount,
              })}
            </Button>
          </DialogTrigger>
        </EditPdfConfirmationDialog>
      </div>
      <Separator />
      <div className="bg-gray-100 py-1 flex-1 h-full overflow-hidden">
        {isInitializing ? (
          <LoadingEditPdfSkeleton />
        ) : (
          <PagesViewer pages={pages} onSplit={handleSplit}>
            {({
              currentPage,
              setCurrentPage,
              setPages,
              setCurrentInvoiceId,
              currentInvoiceId,
            }) => {
              return (
                <PagesPreview
                  pages={pages}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  setPages={setPages}
                  setCurrentInvoiceId={setCurrentInvoiceId}
                  handleMove={handleMove}
                  sensors={sensors}
                  currentInvoiceId={currentInvoiceId}
                  splits={splits}
                  handleSplit={handleSplit}
                  handleDeletePage={handleDeletePage}
                  handleAddInvoice={handleAddInvoice}
                  isAddingPages={isAddingPages}
                  setIsAddingPages={setIsAddingPages}
                  sourceInvoiceId={sourceInvoiceId}
                />
              );
            }}
          </PagesViewer>
        )}
      </div>
    </div>
  );
};

type EditPdfContainerProps = {
  initialPdfBlobs: {
    invoiceId: string;
    fileName: string;
    pdfUrl: string;
    thumbnailUrls?: string[];
  }[];
  sourceInvoiceId?: string;
  onSubmit: (
    layout: { invoiceId: string; pageIndex: number }[][],
    pdfUrls: string[],
  ) => Promise<void>;
  isProcessing: boolean;
};

export const EditPdfContainer = ({
  sourceInvoiceId,
  initialPdfBlobs,
  onSubmit,
}: EditPdfContainerProps) => {
  const [pages, setPages] = useState<EditablePage[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const initializePages = async () => {
      if (initialPdfBlobs.length === 0) {
        return;
      }

      setIsInitializing(true);

      try {
        const allPages = await getPdfPages(initialPdfBlobs);

        setPages(allPages);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setTimeout(() => {
          setIsInitializing(false);
        }, 100);
      }
    };

    initializePages();
  }, [initialPdfBlobs]);

  const handleSave = async (pages: EditablePage[], splits: number[]) => {
    const result: { invoiceId: string; pageIndex: number }[][] = [[]];

    for (let i = 0; i < pages.length; i += 1) {
      const page = pages[i];

      if (!page.markedForDeletion) {
        result[result.length - 1].push({
          invoiceId: page.sourceInvoiceId,
          pageIndex: page.sourcePageIndex,
        });
      }

      if (splits.includes(i)) {
        result.push([]);
      }
    }

    const layout = result.filter((invoice) => invoice.length > 0);

    const pdfUrls: string[] = [];

    for (let groupIndex = 0; groupIndex < layout.length; groupIndex++) {
      const group = layout[groupIndex];

      const groupPages: EditablePage[] = [];

      for (const { invoiceId, pageIndex } of group) {
        const matchingPage = pages.find(
          (page) =>
            page.sourceInvoiceId === invoiceId &&
            page.sourcePageIndex === pageIndex &&
            !page.markedForDeletion,
        );

        if (matchingPage) {
          groupPages.push(matchingPage);
        }
      }

      if (groupPages.length > 0) {
        const pdfUrl = await generatePreviewPdf(groupPages);
        pdfUrls.push(pdfUrl);
      }
    }

    await onSubmit(layout, pdfUrls);
  };

  return (
    <EditPdf
      pages={pages}
      sourceInvoiceId={sourceInvoiceId}
      onPagesChange={setPages}
      onSave={handleSave}
      isInitializing={isInitializing}
    />
  );
};
